import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useReducedMotion } from "framer-motion";
import {
  Activity,
  Check,
  Globe,
  History,
  Rocket,
  SlidersHorizontal,
  Sparkles,
  Target,
  Users,
  type LucideIcon,
} from "lucide-react";

/*
 * Homepage hero diagram: context inputs on the left stream into the Sidestep
 * hub, and each output category on the right receives the features it covers.
 *
 * The animation is plain SVG SMIL (<animateMotion>/<animate>), so it costs no
 * JavaScript per frame and scales with the viewBox. Everything runs on one
 * 14-second cycle: every traveller (chip, dot, streak) repeats with the cycle
 * and its begin offset sets its phase, so the stream is already in steady
 * state when the intro (lines drawing in) finishes. The SVG's timeline is
 * paused while it is off screen, and frozen on a mid-cycle frame when the
 * visitor prefers reduced motion.
 */

type Brand = "meta" | "google" | "context";

interface FlowInput {
  label: string;
  brand: Brand;
  icon?: LucideIcon;
  chips: string[];
}

interface FlowOutput {
  label: string;
  icon: LucideIcon;
  chips: string[];
}

const INPUTS: FlowInput[] = [
  { label: "Meta Ad Creatives", brand: "meta", chips: ["Video", "Carousel", "Image"] },
  { label: "Google Campaign Data", brand: "google", chips: ["Campaign", "Ad group", "Budget"] },
  { label: "Brand & Site Context", brand: "context", icon: Globe, chips: ["Brand voice", "Offers", "Services"] },
  { label: "Meta Performance Metrics", brand: "meta", chips: ["ROAS", "CPA", "CTR"] },
  { label: "Search Terms & Keywords", brand: "google", chips: ["Search term", "Keyword", "Match type"] },
  { label: "Change Log History", brand: "context", icon: History, chips: ["Budget edit", "Bid change", "Paused ad"] },
  { label: "Account Goals & Targets", brand: "context", icon: Target, chips: ["Target CPA", "Target ROAS"] },
];

const OUTPUTS: FlowOutput[] = [
  { label: "Launch", icon: Rocket, chips: ["Bulk Ad Builder", "Campaign Builder", "Ad Templates"] },
  { label: "Manage", icon: SlidersHorizontal, chips: ["Status Changes", "Budget Edits"] },
  { label: "Monitor", icon: Activity, chips: ["Dashboards", "Performance Alerts", "Scheduled Reports"] },
  { label: "Optimize", icon: Sparkles, chips: ["Keyword Optimizer", "Search Ad Analyzer", "Ad Copywriter", "AI Insights"] },
  { label: "Collaborate", icon: Users, chips: ["Tasks", "Team Notes", "Team Access"] },
];

// The figure's only accessible content (the SVG is aria-hidden), so it names
// every input and every output feature the animation shows.
const ARIA_LABEL = `Diagram: Sidestep combines ${INPUTS.map((i) => i.label).join(", ")}, and turns them into five areas: ${OUTPUTS.map((o) => `${o.label} (${o.chips.join(", ")})`).join("; ")}.`;

/* ── Palette (the hero is always light, so literal colours are safe) ── */

const BRAND_COLOR: Record<Brand, string> = {
  meta: "#0866FF",
  google: "#F2A900",
  context: "hsl(252 32% 58%)",
};
const BRAND_ICON_SRC: Partial<Record<Brand, string>> = {
  meta: "/meta-icon.png",
  google: "/google-ads-icon-64.png",
};
/** Output hues run teal → purple down the column, the brand gradient. */
const outputHue = (j: number) => 193 + (j * (282 - 193)) / (OUTPUTS.length - 1);
const outputColor = (j: number, l = 42) => `hsl(${outputHue(j)} 55% ${l}%)`;

const INK = "hsl(220 45% 12%)";
const LINE = "hsl(214 20% 84%)";
const NODE_STROKE = "hsl(214 20% 86%)";
/** Inter: the brand's content font (the app's body font); headlines use Plus Jakarta Sans. */
const FONT = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';

let measureCtx: CanvasRenderingContext2D | null | undefined;
/**
 * Rendered width of a label, so pills fit their text. Measured with the same
 * font stack the SVG uses, so it stays right if Inter fails to load; the
 * diagram re-measures once web fonts are ready (see useFontsReady).
 */
function textWidth(text: string, size: number, weight: number, tracking = 0) {
  if (measureCtx === undefined) {
    measureCtx = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  }
  const spacing = text.length * tracking;
  if (!measureCtx) return text.length * size * 0.56 + spacing;
  measureCtx.font = `${weight} ${size}px ${FONT}`;
  return measureCtx.measureText(text).width + spacing;
}

/* ── Timing ── */

const CYCLE = 14; // seconds — the length of one full loop
/**
 * Mobile output stream: twice the cycle, so its captions (one per feature)
 * each stay up long enough to read.
 */
const MOBILE_OUT_CYCLE = CYCLE * 2;
const INTRO = 0.9; // the stream fades in once the lines have drawn

/* ── Geometry helpers ── */

type Pt = [number, number];
type Seg = { to: Pt; c1?: Pt; c2?: Pt };

interface FlowPath {
  d: string;
  length: number;
  /** arc-length fraction at which `test` first holds */
  fractionWhere: (test: (p: Pt) => boolean) => number;
  /** first sampled point satisfying `test` (the end point if none does) */
  pointWhere: (test: (p: Pt) => boolean) => Pt;
}

function buildPath(start: Pt, segs: Seg[]): FlowPath {
  let d = `M${start[0]},${start[1]}`;
  const pts: Pt[] = [start];
  let cur = start;
  for (const s of segs) {
    if (s.c1 && s.c2) {
      d += ` C${s.c1[0]},${s.c1[1]} ${s.c2[0]},${s.c2[1]} ${s.to[0]},${s.to[1]}`;
      for (let i = 1; i <= 32; i++) {
        const t = i / 32;
        const u = 1 - t;
        pts.push([
          u * u * u * cur[0] + 3 * u * u * t * s.c1[0] + 3 * u * t * t * s.c2[0] + t * t * t * s.to[0],
          u * u * u * cur[1] + 3 * u * u * t * s.c1[1] + 3 * u * t * t * s.c2[1] + t * t * t * s.to[1],
        ]);
      }
    } else {
      d += ` L${s.to[0]},${s.to[1]}`;
      pts.push(s.to);
    }
    cur = s.to;
  }
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  const length = cum[cum.length - 1];
  return {
    d,
    length,
    fractionWhere: (test) => {
      const i = pts.findIndex(test);
      return i < 0 ? 1 : cum[i] / length;
    },
    pointWhere: (test) => pts.find(test) ?? pts[pts.length - 1],
  };
}

const r3 = (n: number) => Math.round(n * 1000) / 1000;

interface Traveller {
  key: string;
  path: FlowPath;
  color: string;
  /** release time within the cycle, seconds */
  release: number;
  /** px per second */
  speed: number;
  /** visible stretch of the path, as arc-length fractions */
  visibleFrom: number;
  visibleTo: number;
  /** streak length in px */
  streak: number;
  label?: string;
  check?: boolean;
  /** output column the traveller feeds (right-hand side only) */
  lane?: number;
  /** loop length in seconds; defaults to CYCLE */
  cycle?: number;
}

/** SMIL timing for one traveller, all relative to a begin that sets its phase. */
function travellerTiming(t: Traveller) {
  const cycle = t.cycle ?? CYCLE;
  const travel = t.path.length / t.speed;
  const begin = `${r3(INTRO + t.release - cycle)}s`;
  const k = (sec: number) => r3(Math.min(Math.max(sec / cycle, 0), 1));
  const fade = 0.3;
  const inA = t.visibleFrom * travel;
  const outB = t.visibleTo * travel;
  const s = t.streak / t.path.length;
  return {
    cycle,
    dur: `${cycle}s`,
    begin,
    motionKeyTimes: `0;${k(travel)};1`,
    opacityKeyTimes: `0;${k(inA)};${k(inA + fade)};${k(outB - fade)};${k(outB)};1`,
    streakFrac: r3(s),
    streakKeyTimes: `0;${k(travel * (1 + s))};1`,
    /** seconds into the cycle at which the streak's head reaches the end */
    arrival: (t.release + travel) % cycle,
  };
}

/** Round-robin release order so consecutive chips use far-apart lanes. */
function schedule<T>(lanes: T[][], laneOrder: number[], passes = 1, cycle = CYCLE) {
  const out: { lane: number; item: T }[] = [];
  const rounds = Math.max(...lanes.map((l) => l.length));
  for (let r = 0; r < rounds * passes; r++) {
    const round = r % rounds;
    for (const lane of laneOrder) if (lanes[lane][round] !== undefined) out.push({ lane, item: lanes[lane][round] });
  }
  return out.map((o, i) => ({ ...o, release: (i * cycle) / out.length }));
}

/** keyTimes/values for a ripple ring that fires at each arrival time. */
function rippleTimeline(arrivals: number[], rFrom: number, rTo: number, cycle = CYCLE) {
  const pts: [number, number, number][] = [[0, rFrom, 0]];
  for (const a of [...arrivals].sort((x, y) => x - y)) {
    const end = Math.min(a + 0.8, cycle - 0.02);
    if (a <= pts[pts.length - 1][0] || end <= a) continue;
    pts.push([a, rFrom, 0], [a + 0.01, rFrom, 0.6], [end, rTo, 0], [end + 0.01, rFrom, 0]);
  }
  pts.push([cycle, rFrom, 0]);
  return {
    dur: `${cycle}s`,
    begin: `${r3(INTRO - cycle)}s`,
    keyTimes: pts.map((p) => r3(p[0] / cycle)).join(";"),
    r: pts.map((p) => p[1]).join(";"),
    opacity: pts.map((p) => p[2]).join(";"),
  };
}

/* ── Shared SVG pieces ── */

function Streak({ t }: { t: Traveller }) {
  const tm = travellerTiming(t);
  return (
    <path
      d={t.path.d}
      pathLength={1}
      fill="none"
      stroke={t.color}
      strokeWidth={1.8}
      strokeDasharray={`${tm.streakFrac} 3`}
      strokeDashoffset={tm.streakFrac}
      opacity={0.85}
    >
      <animate
        attributeName="stroke-dashoffset"
        values={`${tm.streakFrac};-1;-1`}
        keyTimes={tm.streakKeyTimes}
        dur={tm.dur}
        begin={tm.begin}
        repeatCount="indefinite"
      />
    </path>
  );
}

function Mover({ t, children }: { t: Traveller; children: ReactNode }) {
  const tm = travellerTiming(t);
  return (
    <g opacity={0}>
      <animateMotion
        path={t.path.d}
        keyPoints="0;1;1"
        keyTimes={tm.motionKeyTimes}
        calcMode="linear"
        dur={tm.dur}
        begin={tm.begin}
        repeatCount="indefinite"
      />
      <animate
        attributeName="opacity"
        values="0;0;1;1;0;0"
        keyTimes={tm.opacityKeyTimes}
        dur={tm.dur}
        begin={tm.begin}
        repeatCount="indefinite"
      />
      {children}
    </g>
  );
}

const CHIP_FONT = 9.5;
const CHIP_TRACK = 0.4;
const chipWidth = (label: string, withIcon: boolean) =>
  textWidth(label.toUpperCase(), CHIP_FONT, 600, CHIP_TRACK) + (withIcon ? 30 : 26);

/** A chip's pill, centred on (0, 0). Input chips get a dot, output chips a check. */
function ChipBody({ label: raw, color, check }: { label: string; color: string; check?: boolean }) {
  const label = raw.toUpperCase();
  const w = chipWidth(label, !!check);
  const x0 = -w / 2;
  return (
    <>
      <rect x={x0} y={-10} width={w} height={20} rx={10} fill="#fff" stroke={check ? color : NODE_STROKE} strokeOpacity={check ? 0.45 : 1} />
      {check ? (
        <>
          <circle cx={x0 + 11} cy={0} r={5.5} fill={color} />
          <Check x={x0 + 7.5} y={-3.5} width={7} height={7} color="#fff" strokeWidth={4} />
        </>
      ) : (
        <circle cx={x0 + 10} cy={0} r={3} fill={color} />
      )}
      <text
        x={x0 + (check ? 20 : 17)}
        y={0.5}
        dominantBaseline="central"
        fontFamily={FONT}
        fontSize={CHIP_FONT}
        letterSpacing={CHIP_TRACK}
        fill={check ? color : "hsl(216 18% 36%)"}
        fontWeight={600}
      >
        {label}
      </text>
    </>
  );
}

function Chip({ t }: { t: Traveller }) {
  return (
    <Mover t={t}>
      <ChipBody label={t.label ?? ""} color={t.color} check={t.check} />
    </Mover>
  );
}

function Dot({ t, r }: { t: Traveller; r: number }) {
  return (
    <Mover t={t}>
      <circle r={r + 2.5} fill={t.color} opacity={0.18} />
      <circle r={r} fill={t.color} />
    </Mover>
  );
}

/** Intro: the line draws itself in once, then stays. */
function DrawnLine({ d, delay, color = LINE, width = 1.2 }: { d: string; delay: number; color?: string; width?: number }) {
  return (
    <path d={d} pathLength={1} fill="none" stroke={color} strokeWidth={width} strokeDasharray="1 1" strokeDashoffset={1}>
      <animate attributeName="stroke-dashoffset" values="1;0" dur="0.9s" begin={`${delay}s`} fill="freeze" calcMode="spline" keyTimes="0;1" keySplines="0.4 0 0.2 1" />
    </path>
  );
}

function FadeIn({ delay, children }: { delay: number; children: ReactNode }) {
  return (
    <g opacity={0}>
      <animate attributeName="opacity" values="0;1" dur="0.5s" begin={`${delay}s`} fill="freeze" />
      {children}
    </g>
  );
}

function BrandIcon({ brand, icon: Icon, x, y, size }: { brand: Brand; icon?: LucideIcon; x: number; y: number; size: number }) {
  const src = BRAND_ICON_SRC[brand];
  if (src) return <image href={src} x={x} y={y} width={size} height={size} />;
  return Icon ? <Icon x={x} y={y} width={size} height={size} color={BRAND_COLOR[brand]} strokeWidth={2} /> : null;
}

function Hub({ cx, cy, r }: { cx: number; cy: number; r: number }) {
  const dots = 32;
  const orbit = r + 20;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r * 3.6} fill="url(#hf-glow)">
        <animate attributeName="opacity" values="0.75;1;0.75" dur="4s" repeatCount="indefinite" />
      </circle>
      <g>
        <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="40s" repeatCount="indefinite" />
        {Array.from({ length: dots }, (_, i) => {
          const a = (i / dots) * Math.PI * 2;
          const hue = 193 + (Math.abs(Math.sin(a / 2)) * (282 - 193));
          return <circle key={i} cx={r3(cx + Math.cos(a) * orbit)} cy={r3(cy + Math.sin(a) * orbit)} r={1.6} fill={`hsl(${hue} 55% 55%)`} opacity={0.55} />;
        })}
      </g>
      <circle cx={cx} cy={cy} r={r} fill="#fff" stroke={NODE_STROKE} filter="url(#hf-shadow)" />
      <image href="/logo-256.webp" x={cx - r * 0.82} y={cy - r * 0.82} width={r * 1.64} height={r * 1.64} />
    </g>
  );
}

function Defs() {
  return (
    <defs>
      <radialGradient id="hf-glow">
        <stop offset="0%" stopColor="hsl(282 50% 60%)" stopOpacity={0.22} />
        <stop offset="45%" stopColor="hsl(193 63% 58%)" stopOpacity={0.1} />
        <stop offset="100%" stopColor="hsl(193 63% 58%)" stopOpacity={0} />
      </radialGradient>
      <filter id="hf-shadow" x="-20%" y="-30%" width="140%" height="160%">
        <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="hsl(214 28% 26%)" floodOpacity="0.08" />
      </filter>
    </defs>
  );
}

/* ── Desktop (landscape) ── */

const D = { w: 1120, h: 540, cx: 560, cy: 270, hubR: 58 };
const IN_FONT = 13;
const IN_RIGHT = 272;
const OUT_LEFT = 884;
const OUT_W = 196;

function buildDesktopModel() {
  const inRows = INPUTS.map((input, i) => {
    const y = D.cy + (i - 3) * 70;
    const yb = D.cy + (i - 3) * 6;
    const path = buildPath([IN_RIGHT, y], [
      { to: [292, y] },
      { c1: [381, y], c2: [381, yb], to: [470, yb] },
      { to: [D.cx - D.hubR + 6, yb] },
    ]);
    return { input, y, path };
  });
  const outRows = OUTPUTS.map((output, j) => {
    const y = D.cy + (j - 2) * 104;
    const y0 = D.cy + (j - 2) * 6;
    const path = buildPath([D.cx + 50, y0], [
      { c1: [720, y0], c2: [740, y], to: [820, y] },
      { to: [OUT_LEFT - 27, y] },
    ]);
    return { output, y, path };
  });

  const leftTravellers: Traveller[] = schedule(
    INPUTS.map((i) => i.chips),
    [0, 3, 6, 1, 4, 2, 5],
    2,
  ).map(({ lane, item, release }, n) => {
    const { path, input } = inRows[lane];
    // Outer strands converge sooner, so their chips fade out earlier.
    const fadeX = 404 + (3 - Math.abs(lane - 3)) * 18;
    return {
      key: `in-${lane}-${item}-${n}`,
      path,
      color: BRAND_COLOR[input.brand],
      release,
      speed: 72,
      visibleFrom: 0.02,
      visibleTo: path.fractionWhere((p) => p[0] >= fadeX),
      streak: 42,
      label: item,
    };
  });

  const rightTravellers: Traveller[] = schedule(
    OUTPUTS.map((o) => o.chips),
    [0, 2, 4, 1, 3],
  ).map(({ lane, item, release }) => {
    const { path } = outRows[lane];
    const half = chipWidth(item, true) / 2;
    return {
      key: `out-${lane}-${item}`,
      path,
      color: outputColor(lane),
      release,
      speed: 72,
      visibleFrom: path.fractionWhere((p) => p[0] - half >= D.cx + D.hubR - half * 0.6),
      // fade out as the chip's leading edge reaches the check badge
      visibleTo: path.fractionWhere((p) => p[0] + half >= OUT_LEFT - 30),
      streak: 54,
      label: item,
      check: true,
      lane,
    };
  });

  return { inRows, outRows, leftTravellers, rightTravellers };
}

/**
 * Reduced motion: instead of a frozen frame (which would show whichever chips
 * happened to be mid-line), every line carries one fixed example chip, so all
 * seven inputs and all five output areas show what flows along them.
 */
function StaticChips({ model }: { model: ReturnType<typeof buildDesktopModel> }) {
  return (
    <g>
      {model.inRows.map(({ input, path }, i) => {
        const [x, y] = path.pointWhere((p) => p[0] >= 338);
        return (
          <g key={`sin-${i}`} transform={`translate(${r3(x)} ${r3(y)})`}>
            <ChipBody label={input.chips[0]} color={BRAND_COLOR[input.brand]} />
          </g>
        );
      })}
      {model.outRows.map(({ output, path }, j) => {
        const [x, y] = path.pointWhere((p) => p[0] >= 762);
        return (
          <g key={`sout-${j}`} transform={`translate(${r3(x)} ${r3(y)})`}>
            <ChipBody label={output.chips[0]} color={outputColor(j)} check />
          </g>
        );
      })}
    </g>
  );
}

function DesktopDiagram({ model, still }: { model: ReturnType<typeof buildDesktopModel>; still: boolean }) {
  const { inRows, outRows, leftTravellers, rightTravellers } = model;
  return (
    <>
      <Defs />
      {/* Lines */}
      {inRows.map(({ path }, i) => (
        <DrawnLine key={`il-${i}`} d={path.d} delay={0.1 + i * 0.05} />
      ))}
      {outRows.map(({ path }, j) => (
        <DrawnLine key={`ol-${j}`} d={path.d} delay={0.35 + j * 0.05} />
      ))}

      {/* Stream */}
      {still ? (
        <StaticChips model={model} />
      ) : (
        <g opacity={0}>
          <animate attributeName="opacity" values="0;1" dur="0.6s" begin={`${INTRO - 0.2}s`} fill="freeze" />
          {leftTravellers.map((t) => (
            <Streak key={`s-${t.key}`} t={t} />
          ))}
          {rightTravellers.map((t) => (
            <Streak key={`s-${t.key}`} t={t} />
          ))}
          {leftTravellers.map((t) => (
            <Chip key={t.key} t={t} />
          ))}
          {rightTravellers.map((t) => (
            <Chip key={t.key} t={t} />
          ))}
        </g>
      )}

      <Hub cx={D.cx} cy={D.cy} r={D.hubR} />

      {/* Input nodes */}
      {inRows.map(({ input, y }, i) => {
        const label = input.label;
        const w = textWidth(label, IN_FONT, 500) + 52;
        const x = IN_RIGHT - w;
        return (
          <FadeIn key={`in-${i}`} delay={0.05 + i * 0.05}>
            <rect x={x} y={y - 18} width={w} height={36} rx={18} fill="#fff" stroke={NODE_STROKE} filter="url(#hf-shadow)" />
            <BrandIcon brand={input.brand} icon={input.icon} x={x + 13} y={y - 8} size={16} />
            <text x={x + 37} y={y + 0.5} dominantBaseline="central" fontFamily={FONT} fontSize={IN_FONT} fontWeight={500} fill={INK}>
              {label}
            </text>
          </FadeIn>
        );
      })}

      {/* Output nodes */}
      {outRows.map(({ output, y }, j) => {
        const Icon = output.icon;
        const color = outputColor(j);
        const arrivals = rightTravellers
          .filter((t) => t.lane === j)
          .map((t) => travellerTiming(t).arrival);
        const ripple = rippleTimeline(arrivals, 9, 20);
        const bx = OUT_LEFT - 18;
        return (
          <FadeIn key={`out-${j}`} delay={0.45 + j * 0.06}>
            <circle cx={bx} cy={y} r={9} fill="none" stroke={color} strokeWidth={1.5} opacity={0}>
              <animate attributeName="r" values={ripple.r} keyTimes={ripple.keyTimes} dur={ripple.dur} begin={ripple.begin} repeatCount="indefinite" />
              <animate attributeName="opacity" values={ripple.opacity} keyTimes={ripple.keyTimes} dur={ripple.dur} begin={ripple.begin} repeatCount="indefinite" />
            </circle>
            <circle cx={bx} cy={y} r={9} fill={color} />
            <Check x={bx - 5} y={y - 5} width={10} height={10} color="#fff" strokeWidth={3.5} />
            <rect x={OUT_LEFT} y={y - 23} width={OUT_W} height={46} rx={23} fill="#fff" stroke={outputColor(j, 80)} filter="url(#hf-shadow)" />
            <circle cx={OUT_LEFT + 24} cy={y} r={14} fill={outputColor(j, 95)} />
            <Icon x={OUT_LEFT + 16} y={y - 8} width={16} height={16} color={color} strokeWidth={2} />
            <text x={OUT_LEFT + 48} y={y + 0.5} dominantBaseline="central" fontFamily={FONT} fontSize={15} fontWeight={600} fill={INK}>
              {output.label}
            </text>
          </FadeIn>
        );
      })}
    </>
  );
}

/* ── Mobile (portrait) ── */

const M = { w: 360, h: 452, cx: 180, cy: 150, hubR: 44 };
const CAPTION_Y = 426;
const mobileOutX = (j: number) => 36 + j * 72;

/**
 * The phone layout has no room for chips on the lines, so a caption under the
 * tiles names each feature as its dot travels: "Keyword Optimizer → Optimize".
 */
function MobileCaption({ t }: { t: Traveller }) {
  const tm = travellerTiming(t);
  const slot = tm.cycle / (OUTPUTS.reduce((n, o) => n + o.chips.length, 0));
  const k = (sec: number) => r3(sec / tm.cycle);
  const output = OUTPUTS[t.lane ?? 0];
  return (
    <g opacity={0}>
      <animate
        attributeName="opacity"
        values="0;0;1;1;0;0"
        keyTimes={`0;${k(0.1)};${k(0.35)};${k(slot - 0.25)};${k(slot - 0.05)};1`}
        dur={tm.dur}
        begin={tm.begin}
        repeatCount="indefinite"
      />
      <CaptionText feature={t.label ?? ""} color={t.color} category={output.label} />
    </g>
  );
}

function CaptionText({ feature, color, category }: { feature: string; color: string; category: string }) {
  return (
    <text x={M.cx} y={CAPTION_Y} textAnchor="middle" dominantBaseline="central" fontFamily={FONT} fontSize={13}>
      <tspan fontWeight={600} fill={color}>
        {feature}
      </tspan>
      <tspan fill="hsl(216 18% 40%)"> → {category}</tspan>
    </text>
  );
}

function MobileDiagram({ still }: { still: boolean }) {
  const inPaths = INPUTS.map((_, i) => {
    const x = 24 + i * 52;
    const xb = M.cx + (i - 3) * 5;
    return buildPath([x, 0], [{ c1: [x, 60], c2: [xb, 50], to: [xb, 96] }, { to: [xb, M.cy - M.hubR + 6] }]);
  });
  const outPaths = OUTPUTS.map((_, j) => {
    const x0 = M.cx + (j - 2) * 4;
    const x = mobileOutX(j);
    return buildPath([x0, M.cy + M.hubR - 6], [{ c1: [x0, 250], c2: [x, 230], to: [x, 290] }, { to: [x, 300] }]);
  });

  const left: Traveller[] = schedule(INPUTS.map((i) => i.chips), [0, 3, 6, 1, 4, 2, 5]).map(({ lane, item, release }) => ({
    key: `min-${lane}-${item}`,
    path: inPaths[lane],
    color: BRAND_COLOR[INPUTS[lane].brand],
    release,
    speed: 55,
    visibleFrom: 0.05,
    visibleTo: inPaths[lane].fractionWhere((p) => p[1] >= 92),
    streak: 26,
  }));
  const right: Traveller[] = schedule(OUTPUTS.map((o) => o.chips), [0, 2, 4, 1, 3], 1, MOBILE_OUT_CYCLE).map(
    ({ lane, item, release }) => ({
      key: `mout-${lane}-${item}`,
      path: outPaths[lane],
      color: outputColor(lane),
      release,
      speed: 55,
      visibleFrom: outPaths[lane].fractionWhere((p) => p[1] >= M.cy + M.hubR + 4),
      visibleTo: 0.94,
      streak: 30,
      label: item,
      lane,
      cycle: MOBILE_OUT_CYCLE,
    }),
  );

  return (
    <>
      <Defs />
      <defs>
        <linearGradient id="hf-top-fade-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity={0.15} />
          <stop offset="0.12" stopColor="#fff" stopOpacity={1} />
        </linearGradient>
        <mask id="hf-top-fade">
          <rect x={0} y={0} width={M.w} height={M.h} fill="url(#hf-top-fade-g)" />
        </mask>
      </defs>
      <g mask="url(#hf-top-fade)">
        {inPaths.map((p, i) => (
          <DrawnLine key={`mil-${i}`} d={p.d} delay={0.1 + i * 0.04} />
        ))}
      </g>
      {outPaths.map((p, j) => (
        <DrawnLine key={`mol-${j}`} d={p.d} delay={0.35 + j * 0.05} />
      ))}
      <g opacity={0}>
        <animate attributeName="opacity" values="0;1" dur="0.6s" begin={`${INTRO - 0.2}s`} fill="freeze" />
        {[...left, ...right].map((t) => (
          <Streak key={`s-${t.key}`} t={t} />
        ))}
        {[...left, ...right].map((t) => (
          <Dot key={t.key} t={t} r={3} />
        ))}
      </g>
      <Hub cx={M.cx} cy={M.cy} r={M.hubR} />
      {OUTPUTS.map((output, j) => {
        const Icon = output.icon;
        const x = mobileOutX(j);
        const color = outputColor(j);
        const ripple = rippleTimeline(
          right.filter((t) => t.lane === j).map((t) => travellerTiming(t).arrival),
          8,
          17,
          MOBILE_OUT_CYCLE,
        );
        return (
          <FadeIn key={`mo-${j}`} delay={0.45 + j * 0.06}>
            <circle cx={x} cy={310} r={8} fill="none" stroke={color} strokeWidth={1.5} opacity={0}>
              <animate attributeName="r" values={ripple.r} keyTimes={ripple.keyTimes} dur={ripple.dur} begin={ripple.begin} repeatCount="indefinite" />
              <animate attributeName="opacity" values={ripple.opacity} keyTimes={ripple.keyTimes} dur={ripple.dur} begin={ripple.begin} repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy={310} r={8} fill={color} />
            <Check x={x - 4.5} y={305.5} width={9} height={9} color="#fff" strokeWidth={3.5} />
            <rect x={x - 33} y={328} width={66} height={72} rx={14} fill="#fff" stroke={outputColor(j, 80)} filter="url(#hf-shadow)" />
            <circle cx={x} cy={352} r={13} fill={outputColor(j, 95)} />
            <Icon x={x - 7.5} y={344.5} width={15} height={15} color={color} strokeWidth={2} />
            <text x={x} y={384} textAnchor="middle" dominantBaseline="central" fontFamily={FONT} fontSize={10} fontWeight={600} fill={INK}>
              {output.label}
            </text>
          </FadeIn>
        );
      })}
      {still ? (
        // Reduced motion: one example feature per area, in its tile's colour.
        <text x={M.cx} y={CAPTION_Y - 8} textAnchor="middle" fontFamily={FONT} fontSize={12} fontWeight={600}>
          {OUTPUTS.map((o, j) => (
            <tspan key={o.label} x={j === 3 ? M.cx : undefined} dy={j === 3 ? 18 : undefined} fill={outputColor(j)}>
              {o.chips[0]}
              {j !== 2 && j !== OUTPUTS.length - 1 ? <tspan fill="hsl(216 18% 60%)"> · </tspan> : null}
            </tspan>
          ))}
        </text>
      ) : (
        <FadeIn delay={INTRO}>
          {right.map((t) => (
            <MobileCaption key={`cap-${t.key}`} t={t} />
          ))}
        </FadeIn>
      )}
    </>
  );
}

/* ── Wrapper ── */

const DESKTOP_QUERY = "(min-width: 1024px)";

/** Bumps once web fonts have loaded, so label widths are re-measured in Inter. */
function useFontsReady() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;
    let live = true;
    document.fonts
      .load(`500 13px Inter`)
      .then(() => document.fonts.ready)
      .then(() => live && setVersion((v) => v + 1))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return version;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Pause the SMIL timeline off screen; freeze a mid-cycle frame for reduced motion. */
function useTimelineControl(ref: RefObject<SVGSVGElement>, reduceMotion: boolean, layoutKey: boolean) {
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    if (reduceMotion) {
      svg.setCurrentTime(INTRO + 5.5);
      svg.pauseAnimations();
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) svg.unpauseAnimations();
      else svg.pauseAnimations();
    });
    io.observe(svg);
    return () => io.disconnect();
  }, [ref, reduceMotion, layoutKey]);
}

export function HeroFlowDiagram() {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const reduceMotion = !!useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  useTimelineControl(svgRef, reduceMotion, isDesktop);
  const fontsVersion = useFontsReady();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measure when fonts load
  const desktopModel = useMemo(() => buildDesktopModel(), [fontsVersion]);

  if (isDesktop) {
    return (
      <figure className="mx-auto w-full max-w-[1120px]" role="img" aria-label={ARIA_LABEL}>
        <svg key="desktop" ref={svgRef} viewBox={`0 0 ${D.w} ${D.h}`} className="h-auto w-full overflow-visible" aria-hidden="true">
          <DesktopDiagram model={desktopModel} still={reduceMotion} />
        </svg>
      </figure>
    );
  }

  return (
    <figure className="mx-auto w-full max-w-[420px]" role="img" aria-label={ARIA_LABEL}>
      <ul className="flex flex-wrap justify-center gap-1.5 px-1" aria-hidden="true">
        {INPUTS.map((input) => {
          const src = BRAND_ICON_SRC[input.brand];
          const Icon = input.icon;
          return (
            <li
              key={input.label}
              className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-foreground shadow-card"
              style={{ fontFamily: FONT }}
            >
              {src ? (
                <img src={src} alt="" width={12} height={12} className="h-3 w-3" />
              ) : Icon ? (
                <Icon className="h-3 w-3" color={BRAND_COLOR[input.brand]} strokeWidth={2.25} />
              ) : null}
              {input.label}
            </li>
          );
        })}
      </ul>
      <svg key="mobile" ref={svgRef} viewBox={`0 0 ${M.w} ${M.h}`} className="mt-1 h-auto w-full overflow-visible" aria-hidden="true">
        <MobileDiagram still={reduceMotion} />
      </svg>
    </figure>
  );
}

export default HeroFlowDiagram;
